/* eslint-disable @typescript-eslint/no-use-before-define */
import React, { useEffect, useState } from 'react';

import { config } from 'config/config';
import routePaths from 'config/routes';
import moment from 'moment';
import { NextPage } from 'next';
import { useRouter } from 'next/router';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { AnyAction, bindActionCreators, Dispatch } from 'redux';
import { getDataCall } from 'services/api';
import apiPaths from 'services/apiPaths';
import { privateRoute } from 'services/privateRoute';
import { setBreadCrumbs, updateChallenges } from 'src/actions/appActions';
import { setFirstCompletedProfile } from 'src/actions/appActions';
import { getBreadCrumbs, NotFound } from 'src/components';
import { builderChallenge } from 'src/components/challenge/builder/BuilderChallenge';
import BuildPageChallenge, {
  showMoreButton,
} from 'src/components/challenge/ChallengeBuildPage';
import ChallengeCard from 'src/components/challenge/ChallengeCard';
import { IRootReducers } from 'src/reducers';
import { ChallengedTabsEnum, PlatformEnums } from 'src/shared/enums';
import {
  ChallengeCardButton,
  ChallengeDetail,
  IChallenge,
} from 'src/shared/models';
import { appTypes } from 'src/types/appTypes';
import {
  fetchInitialData,
  getTranslatedData,
  renderChallengeCard,
} from 'utils';
import { ReduxPageContext } from 'src/store';
import { RoleEnum } from 'src/shared/enums/role.enum';

const { GET_FORMATIONS } = apiPaths.CHALLENGES;
const { DEFAULT_TIMEZONE } = config.APP;

interface OwnProps {
  children?: any;
}

type Props = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps> &
  OwnProps;

export const renderChallengeFormation = (
  challenge: ChallengeDetail,
): JSX.Element => {
  const { formatMessage } = useIntl();

  let userStatus: { idResource: number; status: number };

  const challengeCardButton: ChallengeCardButton = {
    title: formatMessage({ id: 'formation.see' }),
    path: routePaths.PAGES.FORMATION_DETAIL,
    className: 'challengeCard__link challengeCard__link--active',
  };

  //const challengeTotalPoints =

  const config: ChallengeDetail = {
    ...challenge,
    challengeName: getTranslatedData(challenge, 'name'),
    challengeDescriptionShort: getTranslatedData(challenge, 'descriptionShort'),
    challengeDescription:
      getTranslatedData(challenge, 'description') ||
      getTranslatedData(challenge, 'descriptionLong'),
    challengeCardButton,
    userStatus,
    dontShowLike: true,
    showPoints: getTranslatedData(challenge, 'showPoints'),
    dontShowDates: true,
    cardImage: challenge.cardImageMobile,
  };

  if (
    moment
      .utc(config.challengeDates.startDate, moment.ISO_8601)
      .utcOffset(DEFAULT_TIMEZONE, false)
      .isAfter(moment.utc(Date.now()).utcOffset(DEFAULT_TIMEZONE, false))
  )
    config.challengeCardButton = null;

  return <ChallengeCard {...config} />;
};

const FormationsPage: NextPage<Props> = ({
  formationsList,
  setBreadCrumbs,
  updateChallenges,
  user,
  setFirstCompletedProfile,
  firstCompletedProfile,
}): JSX.Element => {
  const { formatMessage } = useIntl();
  const router = useRouter();

  const [formations, setFormations] = useState<ChallengeDetail[]>();
  const [filters, setFilters] = useState<ChallengedTabsEnum>(
    ChallengedTabsEnum.ALL,
  );

  const [canShowMore, setCanShowMore] = useState<boolean>(
    formationsList.length >= 6,
  );

  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [requestPage, setRequestPage] = useState<number>(2);

  const handleShowMore = async () => {
    setIsLoadingMore(true);
    const request = `${GET_FORMATIONS}?page=${requestPage}&size=3`;

    try {
      const response = await getDataCall({
        dataPath: request,
        callConfig: {},
      });

      const ids = new Set(formations.map((formation) => formation.challengeId));
      const newchallengeAcc: ChallengeDetail[] = [
        ...formationsList,
        ...response.data
          .filter((formation: IChallenge) => !ids.has(formation.idChallenge))
          .map((formation: IChallenge) => builderChallenge(formation, user)),
      ];

      updateChallenges({ formations: newchallengeAcc });

      setRequestPage(requestPage + 1);
      setCanShowMore(response.data.length == 3);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const renderFormations = (): JSX.Element | JSX.Element[] => {
    let filteredFormations: ChallengeDetail[] = [];

    if (!formations?.length)
      return <NotFound message="page.formation.not-found" />;

    // TODO: set new filters
    switch (filters) {
      case ChallengedTabsEnum.PENDING:
        filteredFormations = formations.filter(
          ({ isChallengeCompleted, isCampaignActive }) => !isChallengeCompleted,
        );
        break;
      case ChallengedTabsEnum.COMPLETED:
        filteredFormations = formations.filter(
          ({ isChallengeCompleted }) => isChallengeCompleted,
        );
        break;
      default:
        filteredFormations = formations;
        break;
    }

    if (!filteredFormations.length)
      return <NotFound message="page.formation.not-found" />;

    return filteredFormations.map((challenge: ChallengeDetail) =>
      renderChallengeCard({ challenge }),
    );
  };

  useEffect(() => {
    setFormations(formationsList);
    const breadCrumbs = getBreadCrumbs(router, formatMessage);
    setTimeout(() => {
      setBreadCrumbs(breadCrumbs);
    }, 20);

    // clear breadcrumbs on unmount component
    return () => {
      // TODO: Remove setTimeout => check store.ts
      setTimeout(() => {
        setBreadCrumbs([]);
      }, 20);
    };
  }, [formationsList]);

  useEffect(() => {
    if (firstCompletedProfile) {
      setFirstCompletedProfile(false);
    }
  }, [firstCompletedProfile]);

  const showMoreComponent =
    filters == ChallengedTabsEnum.ALL
      ? showMoreButton(
          `page.marketing-materials.show-more`,
          isLoadingMore,
          canShowMore,
          handleShowMore,
        )
      : null;

  return (
    <>
      <div className={`container challengePage experiencesPage`}>
        <div className="container">
          <div className="title experience-distributor__title">
            {formatMessage({ id: 'formation.distributor-title' })}
          </div>
          <div className="regular-body experience-distributor__description">
            {formatMessage({ id: 'formation.distributor-description' })}
          </div>
        </div>
      </div>
      <BuildPageChallenge
        pageClassName="formationsPage"
        onChangeTab={setFilters}
        renderChallenges={renderFormations}
        showMoreComponent={showMoreComponent}
        filterComponent={null}
        tabs={[
          { key: ChallengedTabsEnum.ALL, label: 'app.all-fem' },
          // { key: ChallengedTabsEnum.PENDING, label: 'app.pending' },
          // TODO: escondidas temporalmente las tabs de 'Mi formación' y 'Formación acreditada' hasta..
          // TODO:.. que se implementen
          /*
          {
            key: ChallengedTabsEnum.MY_FORMATION,
            label: 'formation.distributor.submenu-my.formation',
          },
          {
            key: ChallengedTabsEnum.ACCREDITED_FORMATION,
            label: 'formation.distributor.submenu-my.accredited.formation',
          },
          */
        ]}
      />
    </>
  );
};

// *-------------------------------------------*/
// *------------ GET INITIAL PROPS ------------*/
// *-------------------------------------------*/

const initialAction = async (
  accessToken: string,
  ctx: ReduxPageContext,
): Promise<{ data: unknown }> => {
  try {
    // TODO:Move 6 to config/config.ts
    const user = ctx.store.getState().auth.user;
    if (user?.roles?.primaryRole != RoleEnum.DERMO) return;

    const challenges = await fetchInitialData(
      accessToken,
      `${GET_FORMATIONS}?size=6`,
      ctx,
    );

    const formations: ChallengeDetail[] = challenges.map(
      (challenge: IChallenge) =>
        builderChallenge(challenge, ctx.store.getState().auth.user),
    );

    if (formations)
      ctx.store.dispatch({
        type: appTypes.APP_UPDATE_CHALLENGES,
        payload: { formations },
      });

    return { data: [] };
  } catch (error) {
    console.error('SERVER ERROR: ', error);
    return { data: null };
  }
};

const mapStateToProps = (state: IRootReducers) => {
  return {
    user: state.auth.user,
    firstCompletedProfile: state.app.firstCompletedProfile,
    formationsList: state.app.challenges.formations,
  };
};

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) =>
  bindActionCreators(
    { updateChallenges, setBreadCrumbs, setFirstCompletedProfile },
    dispatch,
  );

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(
  privateRoute({
    WrappedComponent: FormationsPage,
    initialAction,
    platforms: [PlatformEnums.CON],
  }),
);
